/**
 * Menu Filter Utility
 *
 * Filters navigation menu items based on enabled slugs from tenant configuration.
 * Used to control which menu items are visible per tenant.
 */

/**
 * Filters menu configuration based on enabled slugs
 *
 * @param {Array} menuConfig - Full menu configuration from navMenuConfig.js
 * @param {Array|null} enabledSlugs - Array of enabled menu slugs from tenant.enabledMenus or tenant.theme.enabledMenus
 * @returns {Array} Filtered menu items
 *
 * Behavior:
 * - If enabledSlugs is null/undefined/empty, returns full menu (only during initial tenant setup)
 * - For items with submenu: parent is shown if ANY child is enabled
 * - Submenu items are filtered to only show enabled children
 * 
 * Note: Tenant data is cached in localStorage, so menus will be filtered correctly even after page refresh
 */
export function filterMenuByEnabledSlugs(menuConfig, enabledSlugs, tenantSettings) {
  // If no config or empty array, show all menus (fallback for edge cases)
  if (
    !enabledSlugs ||
    !Array.isArray(enabledSlugs) ||
    enabledSlugs.length === 0
  ) {
    return menuConfig
  }

  const isSgPeppol = tenantSettings?.enableSgPeppol === true
  // MY Peppol enablement is the explicit `enableMyPeppol` flag. Transitional
  // fallback: when the flag is still absent (column not yet backfilled / stale
  // cached tenant), fall back to the legacy peppolId-presence detection so
  // existing MY tenants never lose their menu; an explicit `false` (admin
  // disabled) is always respected.
  const enableMyPeppol = tenantSettings?.enableMyPeppol
  const hasMyPeppol =
    enableMyPeppol === true ||
    (enableMyPeppol == null && !!tenantSettings?.peppolId && !isSgPeppol)

  // Every navigable leaf is a key in the api-core menu registry
  // (shared/config/menu-registry.ts), so admins control visibility through
  // theme.enabledMenus. Tenant feature flags below are a jurisdiction safety net
  // only: they can hide an item, never force one on.
  const apDocumentsSync = tenantSettings?.enableApDocumentsSync === true

  // Peppol reporting surface (dashboard + reports), shown only when opted in.
  const peppolReportingSlugs = [
    'peppol-dashboard',
    'peppol-reporting',
    'peppol-transmission-status-report',
    'peppol-monthly-compliance-report',
    'peppol-reconciliation-report',
  ]

  const myPeppolReportingSlugs = [
    'my-peppol-dashboard',
    'my-peppol-reporting',
    'my-peppol-transmission-status-report',
    'my-peppol-monthly-compliance-report',
    'my-peppol-reconciliation-report',
  ]

  // LHDN-specific surface, swapped out when the tenant is on Peppol.
  // Note: e-invoices and document-ledger-report are channel-aware (they adapt to
  // LHDN vs IRSP), so they remain visible to both.
  const lhdnReportingSlugs = [
    'e-invoice-dashboard',
    'reconciliation-report',
    'edocument-by-status-report',
    'proof-of-income-validation-report',
    'proof-of-income-monthly-report',
    'proof-of-expenses-validation-report',
    'email-logs-report',
    'ap-documents-report',
    'lhdn-received-ap',
  ]

  // Purchases > Expense Doc. MY reads the MyInvois AP sync, SG the InvoiceNow
  // inbound; Debit/Refund Notes don't exist on the SG side.
  const expenseDocSlugs = [
    'expense-invoices',
    'expense-credit-notes',
    'expense-debit-notes',
    'expense-refund-notes',
  ]
  const myOnlyExpenseDocSlugs = ['expense-debit-notes', 'expense-refund-notes']

  // Self-billed is a Malaysia (LHDN) construct — Singapore tenants don't issue
  // self-billed e-invoices, so hide the whole Self-Billed Doc group for them.
  const selfBilledSlugs = [
    'self-billed',
    'self-billed-invoices',
    'self-billed-credit-notes',
    'self-billed-debit-notes',
    'self-billed-refund-notes',
  ]


  const sgPeppolHiddenSlugs = isSgPeppol
    ? ['lhdn-submissions', 'peppol-submissions', 'consolidated', ...selfBilledSlugs, ...lhdnReportingSlugs, ...myPeppolReportingSlugs, ...myOnlyExpenseDocSlugs]
    : [
        'sg-peppol-submissions',
        'received-orders',
        'received-invoices',
        'orders',
        ...peppolReportingSlugs,
        ...(hasMyPeppol ? [] : ['peppol-submissions']),
      ]

  const myPeppolHiddenSlugs = hasMyPeppol
    ? []
    : [...myPeppolReportingSlugs, 'my-peppol-received-invoices']

  // MY: without the MyInvois AP sync there are no received documents to list.
  const apDocumentsHiddenSlugs =
    apDocumentsSync || isSgPeppol ? [] : ['lhdn-received-ap', 'ap-documents-report', ...expenseDocSlugs]

  const isHidden = (slug) =>
    sgPeppolHiddenSlugs.includes(slug) ||
    myPeppolHiddenSlugs.includes(slug) ||
    apDocumentsHiddenSlugs.includes(slug)

  // Recursively filter the tree so nested groups resolve at any depth
  // (e.g. Sales > Billing Doc > Invoices, or e-Invoice Exchange >
  // Peppol e-Invoice > Received Orders). A group is kept only when it still
  // has at least one visible descendant, so empty parents never render.
  const filterNode = (item) => {
    // Section headers are layout-only — keep them, prune orphans afterwards.
    if (item.header) return item

    if (item.submenu && item.submenu.length > 0) {
      const submenu = item.submenu.map(filterNode).filter(Boolean)
      return submenu.length > 0 ? { ...item, submenu } : null
    }

    // Leaf: jurisdiction hides win first, then the DB enabled allow-list.
    if (isHidden(item.slug)) return null
    // e-Invoice Exchange channel document lists. Each channel's 'e-Invoices'
    // points at the shared, channel-aware /e-invoices list and is gated by
    // jurisdiction (the shared 'e-invoices' key must be enabled). These are
    // invented layout slugs (not real enabledMenus keys), so they MUST resolve
    // here rather than fall through to the allow-list check below.
    if (item.slug === 'lhdn-e-invoice') {
      return !isSgPeppol && enabledSlugs.includes('e-invoices') ? item : null
    }
    if (item.slug === 'my-peppol-e-invoice') {
      return hasMyPeppol && enabledSlugs.includes('e-invoices') ? item : null
    }
    if (item.slug === 'sg-peppol-e-invoice') {
      return isSgPeppol && enabledSlugs.includes('e-invoices') ? item : null
    }
    return enabledSlugs.includes(item.slug) ? item : null
  }

  const filtered = menuConfig.map(filterNode).filter(Boolean)

  // Drop section headers whose section came out empty (a header immediately
  // followed by another header, or a header left trailing at the end).
  return filtered.filter(
    (item, i) => !item.header || (filtered[i + 1] && !filtered[i + 1].header),
  )
}

/**
 * Gets all available menu slugs from the config
 * Useful for admin UI to show all possible options
 *
 * @param {Array} menuConfig - Full menu configuration
 * @returns {Array} Array of { slug, name, isSubmenu, parentSlug } objects
 */
export function getAllMenuSlugs(menuConfig) {
  const slugs = []

  // Walk the tree to any depth and collect the navigable (leaf) slugs.
  const walk = (items, parentName) => {
    items.forEach((item) => {
      if (item.submenu && item.submenu.length > 0) {
        walk(item.submenu, item.name)
      } else {
        slugs.push({
          slug: item.slug,
          name: item.name,
          isSubmenu: parentName !== null,
          parentName,
        })
      }
    })
  }

  walk(menuConfig, null)
  return slugs
}
